import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import { PRODUCT_PICK_MIN_LENGTH, searchProductPicks } from '@/pages/fulfillment/components/productPick';
import type { ProductPick } from '@/pages/fulfillment/components/productPick';
import type { RegisterUnrecognizedScanRequest } from '@/infrastructure/FulfillmentAdminClient';
import { asciiScanField } from '@/pages/fulfillment/components/asciiScanField';
import { readShelfCode, SHELF_CODE_RULE } from '../shelfCode';

/** The parcel in the operator's hand: the code that would not place, and whatever the screen already knows about it. */
export interface SendToUnrecognizedTarget {
  code: string;
  trackingNo: string | null;
  orderNo: string | null;
  /** The entry the dead-end scan already parked; describing it keeps one parcel to one row. */
  parkedScanId: string | null;
}

export interface SendToUnrecognizedModalProps {
  target: SendToUnrecognizedTarget;
  busy: boolean;
  onClose: () => void;
  onConfirm: (request: RegisterUnrecognizedScanRequest) => void;
}

/** One product the desk read in the box, with how many of it; the count is kept as typed until it is sent. */
interface ParcelItem {
  pick: ProductPick;
  quantity: string;
}

/** Keystrokes settle for this long before the shop is asked; a word typed at speed is one search, not five. */
const SEARCH_SETTLE_MS = 250;
const SEARCH_ERROR = 'The shop could not be searched — register the parcel without a product name.';
/** A row naming neither a waybill nor an order number can never be worked in the pool [T11]. */
const IDENTIFIER_REQUIRED = 'This parcel needs a waybill or an order number before it can be pooled.';
/** The shelf is read like the order's Shelf (SSD-7442, SSD-7817): a waybill or an order number is not one. */
const LOCATION_SHAPE = `Not a shelf code. Scan or type the shelf the box is on. ${SHELF_CODE_RULE}`;

/**
 * A retail barcode (GTIN-8, UPC-A, EAN-13 with a valid check digit) names a product, not a parcel, so it is
 * not offered as the waybill. Anything else the desk scanned and nothing placed is offered as the label on
 * the box, marked as filled from the scan, and stays the operator's to correct.
 */
const looksLikeProductBarcode = (code: string): boolean => {
  const digits = code.trim();
  if (!/^(\d{8}|\d{12}|\d{13})$/.test(digits)) {
    return false;
  }
  const check = Number(digits[digits.length - 1]);
  const sum = digits
    .slice(0, -1)
    .split('')
    .reverse()
    .reduce((acc, digit, index) => acc + Number(digit) * (index % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
};

/** How a product reads in the list: brand first, like every other fulfillment screen [B]. */
const pickLabel = (pick: ProductPick): string => [pick.brand, pick.name].filter(Boolean).join(' ');

const wholeCount = (value: string): boolean => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1;
};

/**
 * The products are a claim confirmed in the pool later, so they are optional: an unnamed parcel is still tracked.
 * One box may hold several products, and the pool keeps one row per product under one parcel, so the dialog
 * takes a list: each product with its own count, the same product chosen twice counted once over.
 */
export function SendToUnrecognizedModal({ target, busy, onClose, onConfirm }: SendToUnrecognizedModalProps) {
  // The barcode just scanned is what the desk read off this parcel; the waybill already open on the screen
  // is never inherited, because that document belongs to a different parcel.
  const prefilled = !looksLikeProductBarcode(target.code);
  const [trackingNo, setTrackingNo] = useState(prefilled ? target.code : '');
  const [query, setQuery] = useState('');
  const [picks, setPicks] = useState<ProductPick[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [items, setItems] = useState<ParcelItem[]>([]);
  /** Whether the search box is on screen: always while nothing is named, and again when the desk adds another product. */
  const [adding, setAdding] = useState(true);
  /** How many units, on a parcel whose products the desk could not name; the named products carry their own counts. */
  const [quantity, setQuantity] = useState('1');
  const [memo, setMemo] = useState('');
  /** Where the box was put while it waits (SSD-7442); optional, read like the order's Shelf. */
  const [location, setLocation] = useState('');
  /** Numbers each search, so an answer that arrives after a newer query was typed is dropped, not shown. */
  const searchSeq = useRef(0);
  const productRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const hitRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const dialogRef = useModalDialog(onClose);

  // With the waybill already filled from the scan, the product is the first thing left to type.
  useEffect(() => {
    if (prefilled) {
      productRef.current?.focus();
    }
  }, [prefilled]);

  const typed = query.trim();
  const searchable = typed.length >= PRODUCT_PICK_MIN_LENGTH;

  useEffect(() => {
    const seq = ++searchSeq.current;
    if (!searchable) {
      setPicks([]);
      setSearching(false);
      setSearchFailed(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      void searchProductPicks(typed).then(
        (found) => {
          if (seq !== searchSeq.current) {
            return;
          }
          setPicks(found);
          setSearchFailed(false);
          setSearching(false);
        },
        () => {
          if (seq !== searchSeq.current) {
            return;
          }
          // A catalog that is down must not read as "nothing matched" — the operator would name the wrong thing.
          setPicks([]);
          setSearchFailed(true);
          setSearching(false);
        },
      );
    }, SEARCH_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [typed, searchable]);

  const named = items.length > 0;
  const countable = named ? items.every((item) => wholeCount(item.quantity)) : wholeCount(quantity);
  const declaredTracking = trackingNo.trim();
  const identified = declaredTracking !== '' || (target.orderNo ?? '') !== '';
  const declaredLocation = location.trim();
  const declaredShelf = readShelfCode(declaredLocation);
  const locationInvalid = declaredLocation !== '' && declaredShelf === null;
  const blocked = busy || !countable || !identified || locationInvalid;
  /** The desk kept the scanned code as the label on the box, so the code is the waybill and no catalog check applies. */
  const codeIsWaybill = declaredTracking === target.code.trim();

  /** The same product chosen again is one product counted once more, never a second row the pool would have to merge. */
  const choose = (pick: ProductPick) => {
    setItems((current) => {
      const at = current.findIndex((item) => item.pick.sku === pick.sku);
      if (at >= 0) {
        return current.map((item, index) =>
          index === at
            ? { ...item, quantity: String((wholeCount(item.quantity) ? Number(item.quantity) : 0) + 1) }
            : item,
        );
      }
      return [...current, { pick, quantity: '1' }];
    });
    setPicks([]);
    setQuery('');
    setAdding(false);
    // The next frame, once the add button is in the tree, so the keyboard lands on the one thing left to do.
    setTimeout(() => addRef.current?.focus(), 0);
  };

  const addAnother = () => {
    setAdding(true);
    setTimeout(() => productRef.current?.focus(), 0);
  };

  const removeItem = (index: number) => {
    const next = items.filter((_, at) => at !== index);
    setItems(next);
    if (next.length === 0) {
      setAdding(true);
      setTimeout(() => productRef.current?.focus(), 0);
    }
  };

  const setItemQuantity = (index: number, value: string) => {
    setItems((current) => current.map((item, at) => (at === index ? { ...item, quantity: value } : item)));
  };

  const listOpen = picks.length > 0 && !searchFailed;

  /**
   * The search box works like the shop's: ArrowDown walks into the matches, Enter takes the top one, and
   * Escape closes the list first (only a second Escape reaches the dialog and closes it).
   */
  const onQueryKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && listOpen) {
      event.preventDefault();
      hitRefs.current[0]?.focus();
    } else if (event.key === 'Enter' && listOpen) {
      event.preventDefault();
      const top = picks[0];
      if (top) {
        choose(top);
      }
    } else if (event.key === 'Escape' && listOpen) {
      event.preventDefault();
      event.stopPropagation();
      searchSeq.current += 1;
      setPicks([]);
    }
  };
  const onHitKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      hitRefs.current[Math.min(index + 1, picks.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index === 0) {
        productRef.current?.focus();
      } else {
        hitRefs.current[index - 1]?.focus();
      }
    }
  };

  const confirm = () => {
    if (blocked) {
      return;
    }
    const trimmedMemo = memo.trim();
    onConfirm({
      code: target.code,
      // Named products carry their counts one by one; an unnamed parcel is one row with the count typed for it.
      ...(named
        ? { items: items.map((item) => ({ sku: item.pick.sku, quantity: Number(item.quantity) })) }
        : { quantity: Number(quantity) }),
      // What the code was is said outright: a waybill is never read against the catalog, a product barcode is.
      ...(codeIsWaybill
        ? { codeKind: 'WAYBILL' as const }
        : looksLikeProductBarcode(target.code)
          ? { codeKind: 'PRODUCT' as const }
          : {}),
      // Each detail travels only when there is one: an empty string would read as a declared value.
      ...(target.parkedScanId ? { scanId: target.parkedScanId } : {}),
      ...(declaredTracking !== '' ? { trackingNo: declaredTracking } : {}),
      ...(target.orderNo ? { orderNo: target.orderNo } : {}),
      ...(trimmedMemo !== '' ? { memo: trimmedMemo } : {}),
      ...(declaredShelf !== null ? { temporaryLocation: declaredShelf } : {}),
      // A backend that predates `codeKind` still reads the code against the catalog; `force` is what it understands
      // for a code the desk declares as the label on the box, and the newer backend simply has no check to waive.
      ...(codeIsWaybill ? { force: true } : {}),
    });
  };

  const thumb = (pick: ProductPick) =>
    pick.imageUrl ? (
      <img src={pick.imageUrl} alt="" width={40} height={40} loading="lazy" />
    ) : (
      <span className="pool-thumb-empty" aria-hidden="true" />
    );

  const sendLabel = items.length > 1 ? `Send to pool · ${items.length} products` : 'Send to pool';

  return (
    <dialog className="vo-modal pool" ref={dialogRef} aria-label="Send to Unrecognized Pool">
      <header>
        <b>Send to Unrecognized Pool</b>
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body pool-body">
        <div className="pol-scan" role="group" aria-label="Scanned barcode">
          <span className="pol-bars" aria-hidden="true" />
          <div className="pol-scan-text">
            <span className="pol-k">Scanned barcode</span>
            <code className="pol-code">{target.code}</code>
          </div>
          <span className={`pool-tag${target.orderNo ? ' has' : ''}`}>Order No {target.orderNo ?? '—'}</span>
        </div>
        <p className="pol-lead">
          Park this parcel so someone can match it later. A waybill or an order number is the least it needs; naming the
          products lets the pool propose the orders each one could belong to.
        </p>

        <div className="pool-field">
          <label htmlFor="pool-tracking">Tracking No</label>
          <input
            id="pool-tracking"
            type="text"
            aria-label="Tracking No"
            placeholder="The waybill on this parcel"
            autoComplete="off"
            spellCheck={false}
            value={trackingNo}
            onChange={(event) => setTrackingNo(event.target.value)}
          />
          {!identified ? (
            <span className="pool-err">{IDENTIFIER_REQUIRED}</span>
          ) : (
            prefilled &&
            codeIsWaybill && (
              <span className="pool-hint">Filled from the scan. Change it if the label reads differently.</span>
            )
          )}
        </div>

        <div className="pool-field">
          <label htmlFor="pool-product">
            Products in this parcel <span className="pool-opt">optional</span>
          </label>
          {named && (
            <ul className="pool-items" aria-label="Products in this parcel">
              {items.map((item, index) => (
                <li key={item.pick.sku} className="pool-item">
                  <span className="pool-thumb">
                    {item.pick.imageUrl ? (
                      <ProductImageZoom src={item.pick.imageUrl} label={pickLabel(item.pick)} className="is-fill">
                        {thumb(item.pick)}
                      </ProductImageZoom>
                    ) : (
                      thumb(item.pick)
                    )}
                  </span>
                  <span className="pool-chosen-text">
                    <b>{pickLabel(item.pick)}</b>
                    <span>{[item.pick.nameKor, item.pick.size, item.pick.sku].filter(Boolean).join(' · ')}</span>
                  </span>
                  <input
                    type="number"
                    min={1}
                    className="pool-item-qty"
                    aria-label={`Quantity of ${pickLabel(item.pick)}`}
                    value={item.quantity}
                    onChange={(event) => setItemQuantity(index, event.target.value)}
                  />
                  <button
                    type="button"
                    className="pool-item-x"
                    aria-label={`Remove ${pickLabel(item.pick)}`}
                    onClick={() => removeItem(index)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          {named && !adding && (
            <button
              type="button"
              ref={addRef}
              className="btn btn-line btn-sm pool-add"
              aria-label="Add another product"
              onClick={addAnother}
            >
              + Add another product
            </button>
          )}
          {adding && (
            <div className="pool-search">
              <div className="pool-search-row">
                <input
                  id="pool-product"
                  ref={productRef}
                  type="text"
                  role="combobox"
                  aria-label="Product"
                  aria-autocomplete="list"
                  aria-expanded={listOpen}
                  aria-controls="pool-results"
                  placeholder="Search like the shop: product name, brand or SKU"
                  autoComplete="off"
                  spellCheck={false}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onQueryKey}
                />
                {searching && <span className="pol-spin pool-spin" aria-label="Searching" />}
              </div>
              {searchFailed && <span className="pool-err">{SEARCH_ERROR}</span>}
              <span className="pool-live" aria-live="polite">
                {searching
                  ? 'Searching the shop'
                  : listOpen
                    ? `${picks.length} products match`
                    : searchable && !searchFailed
                      ? 'No products match'
                      : ''}
              </span>
              {listOpen && (
                <ul className="pool-results" id="pool-results" role="listbox" aria-label="Matching products">
                  {picks.map((pick, index) => (
                    <li key={pick.sku} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={false}
                        className="pool-hit"
                        ref={(element) => {
                          hitRefs.current[index] = element;
                        }}
                        onKeyDown={(event) => onHitKey(event, index)}
                        onClick={() => choose(pick)}
                      >
                        <span className="pool-thumb">{thumb(pick)}</span>
                        <span className="pool-hit-text">
                          <b>{pickLabel(pick)}</b>
                          <span>{[pick.nameKor, pick.size, pick.sku].filter(Boolean).join(' · ')}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!searchFailed && searchable && !searching && picks.length === 0 && (
                <span className="pool-hint">
                  Nothing in the shop matches “{typed}”. Send it without a product; the pool can still work it.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="pool-field pool-loc">
          <label htmlFor="pool-location">
            Temporary Location <span className="pool-opt">optional</span>
          </label>
          <input
            id="pool-location"
            type="text"
            aria-label="Temporary Location"
            aria-invalid={locationInvalid}
            placeholder="Scan or type the shelf the box is on, e.g. RT-1-303"
            value={location}
            {...asciiScanField(setLocation, (event: KeyboardEvent<HTMLInputElement>) => {
              // A scanner ends the shelf code with Enter: the box is placed, so the parcel is ready to send.
              if (event.key === 'Enter') {
                event.preventDefault();
                confirm();
              }
            })}
          />
          {locationInvalid ? (
            <span className="pool-err" role="alert">
              {LOCATION_SHAPE}
            </span>
          ) : (
            <span className="pool-hint">Where the box waits, so the FC team can fetch it once the order is found.</span>
          )}
        </div>

        <div className="pool-row">
          {!named && (
            <div className="pool-field pool-qty">
              <label htmlFor="pool-qty">Quantity</label>
              <input
                id="pool-qty"
                type="number"
                min={1}
                aria-label="Quantity"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </div>
          )}
          <div className="pool-field pool-memo">
            <label htmlFor="pool-memo">
              Memo <span className="pool-opt">optional</span>
            </label>
            <textarea
              id="pool-memo"
              aria-label="Memo"
              rows={2}
              placeholder="Anything the pool should know: damaged label, two boxes under one number…"
              value={memo}
              onChange={(event) => setMemo(event.target.value)}
            />
          </div>
        </div>
      </div>
      <footer className="pol-footer">
        <span className="pol-hint">An unnamed parcel is still tracked.</span>
        <div className="pool-actions">
          <button type="button" className="btn btn-sm pol-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-blue" disabled={blocked} onClick={confirm}>
            {sendLabel}
          </button>
        </div>
      </footer>
    </dialog>
  );
}
